import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi, useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Users, FileText, AlertTriangle, RefreshCw, Send, FileDown, Pencil } from 'lucide-react';
import { RegistryLink } from '@/components/RegistryLink';
import ClientRiskNotes from '@/components/ClientRiskNotes';
import { DeclarationRegisterCard } from '@/components/DeclarationRegisterCard';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A', bg: '#F5F7FA' };
const fmt$ = v => { if (!v) return '$0'; if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`; if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`; return `$${Math.round(v).toLocaleString()}`; };
const TYPE_STYLE = {
  policy: { color: C.blue, icon: FileText, label: 'Policy' },
  claim: { color: C.red, icon: AlertTriangle, label: 'Claim' },
  renewal: { color: C.teal, icon: RefreshCw, label: 'Renewal' },
  submission: { color: C.amber, icon: Send, label: 'Submission' },
};
const STATUS_COLOR = { active: C.green, settled: C.green, accepted: C.green, bound: C.green, declined: C.red, quote_generated: C.teal, offered: C.blue };

export default function ClientGoldenPage() {
  const { entityId } = useParams();
  const api = useApi();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('timeline');
  const [editing, setEditing] = useState(false);

  const loadView = () => api.get(`/entities/${entityId}/golden-view`)
    .then(r => setView(r.data))
    .catch(err => setError(err?.response?.data?.detail || 'Failed to load client'));

  useEffect(() => { loadView(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, entityId]);

  if (error) return <div className="p-6 text-sm" style={{ color: C.red }} data-testid="golden-view-error">{error}</div>;
  if (!view) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 rounded-md" />)}</div>;

  const g = view.golden;
  const s = view.summary;

  return (
    <div className="space-y-4" data-testid="client-golden-page">
      <div className="flex items-start gap-3">
        <button onClick={() => navigate(-1)} className="p-2 rounded-md hover:bg-gray-100 mt-0.5"><ArrowLeft className="w-4 h-4" style={{ color: C.blue }} /></button>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold" style={{ color: C.navy }} data-testid="golden-client-name">{g.legal_name}</h2>
            <span className="px-2 py-0.5 rounded text-[10px] font-semibold" style={{ background: 'rgba(29,158,117,0.12)', color: C.green }}>GOLDEN RECORD</span>
            {g.abn_source === 'simulated' && (
              <span className="px-2 py-0.5 rounded text-[10px] font-semibold" style={{ background: 'rgba(224,122,47,0.12)', color: '#E07A2F' }} data-testid="abn-unverified-badge"
                title="No ABN was supplied when this business was created — a simulated placeholder was issued. Edit the business to add the real ABN.">
                ABN UNVERIFIED (SIMULATED)
              </span>
            )}
            {view.aliases.length > 0 && (
              <span className="px-2 py-0.5 rounded text-[10px] font-medium" style={{ background: 'rgba(26,76,124,0.08)', color: C.blue }} data-testid="alias-count">
                <Users className="w-3 h-3 inline mr-1" />{view.aliases.length} merged {view.aliases.length === 1 ? 'alias' : 'aliases'}
              </span>
            )}
          </div>
          <p className="text-xs mt-0.5" style={{ color: C.slate }}>
            <RegistryLink abn={g.abn} nzbn={g.nzbn} style={{ color: C.slate }} />
            {g.industry_code ? ` · ${g.industry_code}` : ''}{g.state ? ` · ${g.suburb || ''} ${g.state}` : ''}
          </p>
          {view.aliases.length > 0 && (
            <p className="text-[10px] mt-1 truncate" style={{ color: '#64748B' }}>
              Also known as: {view.aliases.map(a => a.legal_name).slice(0, 4).join(' · ')}{view.aliases.length > 4 ? ` +${view.aliases.length - 4} more` : ''}
            </p>
          )}
        </div>
        <button onClick={() => setEditing(!editing)} data-testid="edit-client-btn"
          className="flex items-center gap-1.5 px-3.5 py-2 rounded-md text-xs font-semibold shrink-0 transition-colors hover:bg-[rgba(26,76,124,0.08)]"
          style={{ color: C.blue, border: '1px solid rgba(26,76,124,0.3)' }} title="Edit business details">
          <Pencil className="w-3.5 h-3.5" />{editing ? 'Close' : 'Edit Details'}
        </button>
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/loss-run/${g.entity_id}`}
          target="_blank" rel="noopener noreferrer" data-testid="loss-run-btn"
          className="flex items-center gap-1.5 px-3.5 py-2 rounded-md text-xs font-semibold text-white shrink-0 transition-opacity hover:opacity-90"
          style={{ background: '#1A4C7C' }} title="Download the loss run report (claims history & incurred totals)">
          <FileDown className="w-3.5 h-3.5" />Loss Run PDF
        </a>
      </div>

      {editing && <EditClientCard api={api} entity={g} onSaved={() => { setEditing(false); loadView(); }} />}

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <KPI label="Active Policies" value={s.active_policies} color={C.blue} />
        <KPI label="Total GWP" value={fmt$(s.total_gwp)} color={C.navy} />
        <KPI label="Claims" value={s.total_claims} color={s.total_claims > 0 ? C.amber : C.slate} />
        <KPI label="Open Claims" value={s.open_claims} color={s.open_claims > 0 ? C.red : C.green} />
        <KPI label="Claims Paid" value={fmt$(s.claims_paid)} color={C.slate} />
        <KPI label="Renewals" value={s.renewals} color={C.teal} />
      </div>

      <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
        <ClientRiskNotes entityId={g.entity_id} editable />
      </Card>

      <div className="flex gap-1 border-b" style={{ borderColor: 'rgba(136,150,168,0.15)' }}>
        {[['timeline', `Timeline (${view.timeline.length})`], ['policies', `Policies (${view.policies.length})`], ['claims', `Claims (${view.claims.length})`], ['renewals', `Renewals (${view.renewals.length})`], ['declarations', 'Declarations']].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`golden-tab-${id}`}
            className="px-4 py-2 text-xs font-medium transition-colors"
            style={{ color: tab === id ? C.blue : C.slate, borderBottom: tab === id ? `2px solid ${C.blue}` : '2px solid transparent' }}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'timeline' && (
        <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="golden-timeline">
          {view.timeline.length === 0 ? <p className="text-sm py-4 text-center" style={{ color: C.slate }}>No history for this client yet.</p> : (
            <div className="space-y-0">
              {view.timeline.map((t, i) => {
                const ts = TYPE_STYLE[t.type] || TYPE_STYLE.policy;
                const Icon = ts.icon;
                return (
                  <div key={i} className="flex items-center gap-3 py-2 border-b" style={{ borderColor: 'rgba(136,150,168,0.06)' }}>
                    <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0" style={{ background: ts.color + '12' }}>
                      <Icon className="w-3 h-3" style={{ color: ts.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate" style={{ color: C.navy }}>{t.label}
                        {t.sim && <span className="ml-1.5 px-1 py-px rounded text-[8px] font-bold" style={{ background: 'rgba(0,180,216,0.12)', color: '#0E7490' }}>SIM</span>}
                      </p>
                      <p className="text-[10px]" style={{ color: C.slate }}>{t.date ? new Date(t.date).toLocaleDateString('en-AU') : '—'}</p>
                    </div>
                    {t.amount != null && <span className="text-xs font-mono shrink-0" style={{ color: C.navy }}>{fmt$(t.amount)}</span>}
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-medium capitalize shrink-0" style={{ background: (STATUS_COLOR[t.status] || C.slate) + '15', color: STATUS_COLOR[t.status] || C.slate }}>{(t.status || '').replace(/_/g, ' ')}</span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {tab === 'policies' && <ListCard rows={view.policies} empty="No policies" render={p => [p.policy_number, p.class, fmt$(p.annual_premium), p.expiry_date ? `exp ${new Date(p.expiry_date).toLocaleDateString('en-AU')}` : '', p.status]} testid="golden-policies" />}
      {tab === 'claims' && <ListCard rows={view.claims} empty="No claims" render={c => [c.claim_reference, c.claim_type, fmt$(c.estimated_quantum), `paid ${fmt$(c.paid_amount)}`, c.status]} testid="golden-claims" />}
      {tab === 'renewals' && <ListCard rows={view.renewals} empty="No renewals" render={r => [r.renewal_reference, r.insurance_class, fmt$(r.renewal_premium), r.current_expiry ? `exp ${new Date(r.current_expiry).toLocaleDateString('en-AU')}` : '', r.status]} testid="golden-renewals" />}
      {tab === 'declarations' && <DeclarationRegisterCard api={api} entityId={entityId} isAdmin={['platform_owner', 'mga_admin'].includes(user?.role)} />}
    </div>
  );
}

function EditClientCard({ api, entity, onSaved }) {
  const [form, setForm] = useState({
    legal_name: entity.legal_name || '', trading_name: entity.trading_name || '',
    industry_code: entity.industry_code || '', street_address: entity.street_address || '',
    suburb: entity.suburb || '', state: entity.state || '', postcode: entity.postcode || '',
    employee_count: entity.employee_count ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const inputCls = 'px-3 py-2 text-xs rounded-sm border w-full focus:outline-none focus:ring-1 focus:ring-[#1A4C7C]';
  const inputStyle = { borderColor: 'rgba(136,150,168,0.3)', color: C.navy };

  const save = async () => {
    setBusy(true); setErr('');
    try {
      await api.put(`/entities/${entity.entity_id}`, {
        ...form,
        employee_count: form.employee_count === '' ? null : parseInt(form.employee_count),
      });
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || 'Failed to save changes'); }
    setBusy(false);
  };

  return (
    <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(26,76,124,0.25)' }} data-testid="edit-client-card">
      <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: C.navy }}>Edit Business Details</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <div className="md:col-span-2">
          <label className="text-[10px]" style={{ color: C.slate }}>Legal name</label>
          <input className={inputCls} style={inputStyle} value={form.legal_name} onChange={e => set('legal_name', e.target.value)} data-testid="edit-client-name-input" />
        </div>
        <div>
          <label className="text-[10px]" style={{ color: C.slate }}>Trading name</label>
          <input className={inputCls} style={inputStyle} value={form.trading_name} onChange={e => set('trading_name', e.target.value)} data-testid="edit-client-trading-input" />
        </div>
        <div>
          <label className="text-[10px]" style={{ color: C.slate }}>Industry</label>
          <input className={inputCls} style={inputStyle} value={form.industry_code} onChange={e => set('industry_code', e.target.value)} data-testid="edit-client-industry-input" />
        </div>
        <div>
          <label className="text-[10px]" style={{ color: C.slate }}>Street address</label>
          <input className={inputCls} style={inputStyle} value={form.street_address} onChange={e => set('street_address', e.target.value)} data-testid="edit-client-street-input" />
        </div>
        <div>
          <label className="text-[10px]" style={{ color: C.slate }}>Suburb</label>
          <input className={inputCls} style={inputStyle} value={form.suburb} onChange={e => set('suburb', e.target.value)} data-testid="edit-client-suburb-input" />
        </div>
        <div>
          <label className="text-[10px]" style={{ color: C.slate }}>State</label>
          <input className={inputCls} style={inputStyle} value={form.state} onChange={e => set('state', e.target.value)} data-testid="edit-client-state-input" />
        </div>
        <div>
          <label className="text-[10px]" style={{ color: C.slate }}>Postcode</label>
          <input className={inputCls} style={inputStyle} value={form.postcode} onChange={e => set('postcode', e.target.value)} data-testid="edit-client-postcode-input" />
        </div>
        <div>
          <label className="text-[10px]" style={{ color: C.slate }}>Employees</label>
          <input type="number" min="0" className={inputCls} style={inputStyle} value={form.employee_count} onChange={e => set('employee_count', e.target.value)} data-testid="edit-client-employees-input" />
        </div>
      </div>
      {err && <p className="text-xs mt-2" style={{ color: C.red }} data-testid="edit-client-error">{err}</p>}
      <div className="flex gap-2 mt-3">
        <button onClick={save} disabled={busy || form.legal_name.trim().length < 3} data-testid="edit-client-save-btn"
          className="px-4 py-2 rounded-sm text-xs font-semibold text-white disabled:opacity-40" style={{ background: C.blue }}>
          {busy ? 'Saving…' : 'Save Changes'}
        </button>
      </div>
    </Card>
  );
}

function KPI({ label, value, color }) {
  return (<Card className="px-3 py-2.5 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
    <p className="text-[9px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{label}</p>
    <p className="text-base font-semibold mt-0.5 font-mono" style={{ color }}>{value}</p>
  </Card>);
}

function ListCard({ rows, empty, render, testid }) {
  return (
    <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid={testid}>
      {rows.length === 0 ? <p className="text-sm py-4 text-center" style={{ color: C.slate }}>{empty}</p> : (
        <div className="space-y-0">
          {rows.map((r, i) => {
            const cols = render(r);
            const status = cols[cols.length - 1];
            return (
              <div key={i} className="flex items-center gap-4 py-2 border-b text-xs" style={{ borderColor: 'rgba(136,150,168,0.06)' }}>
                <span className="font-mono w-40 truncate" style={{ color: C.blue }}>{cols[0]}</span>
                <span className="flex-1 truncate" style={{ color: C.navy }}>{cols[1]}</span>
                <span className="font-mono" style={{ color: C.navy }}>{cols[2]}</span>
                <span className="hidden md:inline text-[10px]" style={{ color: C.slate }}>{cols[3]}</span>
                <span className="px-1.5 py-0.5 rounded text-[9px] font-medium capitalize shrink-0" style={{ background: (STATUS_COLOR[status] || C.slate) + '15', color: STATUS_COLOR[status] || C.slate }}>{(status || '').replace(/_/g, ' ')}</span>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
